import React, { useState } from 'react';
import { FilterIcon, EyeIcon, DownloadIcon, SearchIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import StatusBadge from '../../components/ui/StatusBadge';
import DateRangePicker from '../../components/ui/DateRangePicker';
import ExportMenu from '../../components/ui/ExportMenu';
import Modal from '../../components/ui/Modal';
// Mock audit log data
const auditLogsData = [{
  id: '1',
  timestamp: '2023-07-15T10:30:00Z',
  userName: 'John Smith',
  userId: 'U1023',
  role: 'Admin',
  action: 'Created Merchant',
  module: 'Merchant Profile',
  objectId: 'M2034',
  ipAddress: '192.168.1.1',
  status: 'success',
  remarks: 'New merchant onboarding',
  details: {
    before: null,
    after: {
      id: 'M2034',
      name: 'Coffee Shop Inc',
      status: 'active',
      createdAt: '2023-07-15T10:30:00Z',
      createdBy: 'U1023'
    }
  }
}, {
  id: '2',
  timestamp: '2023-07-15T11:45:00Z',
  userName: 'Jane Doe',
  userId: 'U1024',
  role: 'Manager',
  action: 'Updated Limits',
  module: 'Merchant Settings',
  objectId: 'M2034',
  ipAddress: '192.168.1.2',
  status: 'success',
  remarks: 'Increased transaction limit',
  details: {
    before: {
      dailyLimit: 5000,
      monthlyLimit: 100000
    },
    after: {
      dailyLimit: 10000,
      monthlyLimit: 200000
    }
  }
}, {
  id: '3',
  timestamp: '2023-07-15T12:15:00Z',
  userName: 'Robert Johnson',
  userId: 'U1025',
  role: 'Agent',
  action: 'Login Attempt',
  module: 'Authentication',
  objectId: 'U1025',
  ipAddress: '192.168.1.3',
  status: 'failed',
  remarks: 'Invalid credentials',
  details: {
    reason: 'Invalid password',
    attempts: 2
  }
}, {
  id: '4',
  timestamp: '2023-07-15T13:30:00Z',
  userName: 'Sarah Williams',
  userId: 'U1026',
  role: 'Auditor',
  action: 'Exported Report',
  module: 'Reports',
  objectId: 'R1001',
  ipAddress: '192.168.1.4',
  status: 'success',
  remarks: 'Monthly transaction report',
  details: {
    reportType: 'Monthly Transactions',
    format: 'CSV',
    filters: {
      dateRange: 'July 2023',
      merchants: 'All'
    }
  }
}, {
  id: '5',
  timestamp: '2023-07-15T14:45:00Z',
  userName: 'Michael Brown',
  userId: 'U1027',
  role: 'Admin',
  action: 'Deleted User',
  module: 'User Management',
  objectId: 'U1028',
  ipAddress: '192.168.1.5',
  status: 'success',
  remarks: 'User no longer with company',
  details: {
    before: {
      id: 'U1028',
      name: 'James Wilson',
      email: 'james@example.com',
      role: 'Agent',
      status: 'active'
    },
    after: null
  }
}, {
  id: '6',
  timestamp: '2023-07-15T15:00:00Z',
  userName: 'Emily Davis',
  userId: 'U1029',
  role: 'Manager',
  action: 'Generated QR Code',
  module: 'QRC Generator',
  objectId: 'Q5001',
  ipAddress: '192.168.1.6',
  status: 'success',
  remarks: 'For merchant point of sale',
  details: {
    merchantId: 'M2035',
    qrType: 'Static',
    amount: null
  }
}, {
  id: '7',
  timestamp: '2023-07-15T16:15:00Z',
  userName: 'David Wilson',
  userId: 'U1030',
  role: 'Checker',
  action: 'Approved Merchant',
  module: 'Merchant Approval',
  objectId: 'M2036',
  ipAddress: '192.168.1.7',
  status: 'success',
  remarks: 'All documents verified',
  details: {
    before: {
      status: 'pending'
    },
    after: {
      status: 'approved'
    }
  }
}, {
  id: '8',
  timestamp: '2023-07-15T17:30:00Z',
  userName: 'Linda Miller',
  userId: 'U1031',
  role: 'Maker',
  action: 'Created Role',
  module: 'Role Management',
  objectId: 'R101',
  ipAddress: '192.168.1.8',
  status: 'success',
  remarks: 'New support role',
  details: {
    roleName: 'Support Agent',
    permissions: ['view_merchants', 'view_transactions', 'create_tickets']
  }
}, {
  id: '9',
  timestamp: '2023-07-15T18:45:00Z',
  userName: 'James Taylor',
  userId: 'U1032',
  role: 'Admin',
  action: 'System Configuration',
  module: 'Settings',
  objectId: 'S001',
  ipAddress: '192.168.1.9',
  status: 'failed',
  remarks: 'Invalid configuration',
  details: {
    error: 'Invalid timeout value',
    submitted: {
      sessionTimeout: -1
    }
  }
}, {
  id: '10',
  timestamp: '2023-07-15T19:00:00Z',
  userName: 'Patricia Anderson',
  userId: 'U1033',
  role: 'Checker',
  action: 'Rejected Transaction',
  module: 'Transaction Approval',
  objectId: 'T10001',
  ipAddress: '192.168.1.10',
  status: 'success',
  remarks: 'Suspicious activity',
  details: {
    transactionAmount: 50000,
    merchant: 'M2037',
    reason: 'Amount exceeds usual pattern'
  }
}];
interface AuditLog {
  id: string;
  timestamp: string;
  userName: string;
  userId: string;
  role: string;
  action: string;
  module: string;
  objectId: string;
  ipAddress: string;
  status: string;
  remarks: string;
  details: any;
}
const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>(auditLogsData);
  const [filteredLogs, setFilteredLogs] = useState<AuditLog[]>(auditLogsData);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFailedOnly, setShowFailedOnly] = useState(false);
  // Filter states
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [userFilter, setUserFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [ipFilter, setIpFilter] = useState('');
  const handleViewDetails = (log: AuditLog) => {
    setSelectedLog(log);
    setShowDetailModal(true);
  };
  const handleExport = (format: 'csv' | 'xlsx' | 'pdf') => {
    console.log(`Exporting audit logs as ${format}`);
    // Implement export logic here
  };
  const applyFilters = () => {
    let filtered = [...logs];
    if (startDate) {
      filtered = filtered.filter(log => new Date(log.timestamp) >= startDate);
    }
    if (endDate) {
      filtered = filtered.filter(log => new Date(log.timestamp) <= endDate);
    }
    if (userFilter) {
      filtered = filtered.filter(log => log.userName.toLowerCase().includes(userFilter.toLowerCase()) || log.userId.toLowerCase().includes(userFilter.toLowerCase()));
    }
    if (roleFilter) {
      filtered = filtered.filter(log => log.role === roleFilter);
    }
    if (moduleFilter) {
      filtered = filtered.filter(log => log.module === moduleFilter);
    }
    if (actionFilter) {
      filtered = filtered.filter(log => log.action.toLowerCase().includes(actionFilter.toLowerCase()));
    }
    if (ipFilter) {
      filtered = filtered.filter(log => log.ipAddress.includes(ipFilter));
    }
    if (showFailedOnly) {
      filtered = filtered.filter(log => log.status === 'failed');
    }
    setFilteredLogs(filtered);
  };
  const resetFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setUserFilter('');
    setRoleFilter('');
    setModuleFilter('');
    setActionFilter('');
    setIpFilter('');
    setShowFailedOnly(false);
    setFilteredLogs(logs);
  };
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <StatusBadge status="success" text="Success" data-id="element-6527" />;
      case 'failed':
        return <StatusBadge status="danger" text="Failed" data-id="element-6528" />;
      default:
        return <StatusBadge status="default" text={status} data-id="element-6529" />;
    }
  };
  const getActionBadge = (action: string) => {
    let status: 'success' | 'warning' | 'danger' | 'info' | 'default' = 'default';
    if (action.includes('Create') || action.includes('Add') || action.includes('Generate')) {
      status = 'success';
    } else if (action.includes('Update') || action.includes('Edit') || action.includes('Modify')) {
      status = 'info';
    } else if (action.includes('Delete') || action.includes('Remove')) {
      status = 'danger';
    } else if (action.includes('Approve') || action.includes('Reject') || action.includes('Export')) {
      status = 'warning';
    }
    return <StatusBadge status={status} text={action} data-id="element-6530" />;
  };
  // Get unique values for filters
  const uniqueRoles = Array.from(new Set(logs.map(log => log.role)));
  const uniqueModules = Array.from(new Set(logs.map(log => log.module)));
  const columns = [{
    header: 'Timestamp',
    accessor: (log: AuditLog) => formatDateTime(log.timestamp),
    sortable: true
  }, {
    header: 'User',
    accessor: (log: AuditLog) => <div data-id="element-6531">
          <div className="font-medium" data-id="element-6532">{log.userName}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-6533">
            {log.userId}
          </div>
        </div>,
    sortable: false
  }, {
    header: 'Role',
    accessor: 'role',
    sortable: true
  }, {
    header: 'Action',
    accessor: (log: AuditLog) => getActionBadge(log.action),
    sortable: false
  }, {
    header: 'Module',
    accessor: 'module',
    sortable: true
  }, {
    header: 'Object ID',
    accessor: 'objectId',
    sortable: true
  }, {
    header: 'IP Address',
    accessor: 'ipAddress',
    sortable: true
  }, {
    header: 'Status',
    accessor: (log: AuditLog) => getStatusBadge(log.status),
    sortable: false
  }];
  return <div className="space-y-6" data-id="element-6534">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-6535">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-6536">
          Audit Logs
        </h1>
        <div className="mt-3 sm:mt-0 flex flex-wrap gap-2" data-id="element-6537">
          <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-6538">
            <FilterIcon className="h-4 w-4 mr-2" data-id="element-6539" />
            Filters
          </button>
          <button onClick={() => {
          setLogs(auditLogsData);
          setFilteredLogs(auditLogsData);
        }} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-6540">
            <div className="h-4 w-4 mr-2" data-id="element-6541" />
            Refresh
          </button>
          <ExportMenu onExport={handleExport} data-id="element-6542" />
        </div>
      </div>

      {showFilters && <Card data-id="element-6543">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-6544">
            <div className="md:col-span-3" data-id="element-6545">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6546">
                Date Range
              </label>
              <DateRangePicker startDate={startDate} endDate={endDate} onStartDateChange={setStartDate} onEndDateChange={setEndDate} data-id="element-6547" />
            </div>
            <div data-id="element-6548">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6549">
                User / User ID
              </label>
              <div className="relative" data-id="element-6550">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-6551">
                  <SearchIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" data-id="element-6552" />
                </div>
                <input type="text" className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-md block w-full pl-10 p-2.5" placeholder="Search user or ID" value={userFilter} onChange={e => setUserFilter(e.target.value)} data-id="element-6553" />
              </div>
            </div>
            <div data-id="element-6554">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6555">
                Role
              </label>
              <select className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-md block w-full p-2.5" value={roleFilter} onChange={e => setRoleFilter(e.target.value)} data-id="element-6556">
                <option value="" data-id="element-6557">All Roles</option>
                {uniqueRoles.map(role => <option key={role} value={role} data-id="element-6558">
                    {role}
                  </option>)}
              </select>
            </div>
            <div data-id="element-6559">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6560">
                Module
              </label>
              <select className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-md block w-full p-2.5" value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} data-id="element-6561">
                <option value="" data-id="element-6562">All Modules</option>
                {uniqueModules.map(module => <option key={module} value={module} data-id="element-6563">
                    {module}
                  </option>)}
              </select>
            </div>
            <div data-id="element-6564">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6565">
                Action Type
              </label>
              <input type="text" className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-md block w-full p-2.5" placeholder="e.g. Create, Update, Delete" value={actionFilter} onChange={e => setActionFilter(e.target.value)} data-id="element-6566" />
            </div>
            <div data-id="element-6567">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6568">
                IP Address
              </label>
              <input type="text" className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-md block w-full p-2.5" placeholder="e.g. 192.168.1.1" value={ipFilter} onChange={e => setIpFilter(e.target.value)} data-id="element-6569" />
            </div>
          </div>
          <div className="mt-4 flex items-center" data-id="element-6570">
            <input id="failed-only" type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" checked={showFailedOnly} onChange={e => setShowFailedOnly(e.target.checked)} data-id="element-6571" />
            <label htmlFor="failed-only" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-6572">
              Show Only Failed Actions
            </label>
          </div>
          <div className="mt-4 flex justify-end space-x-3" data-id="element-6573">
            <button onClick={resetFilters} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-6574">
              Reset
            </button>
            <button onClick={applyFilters} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-6575">
              Apply Filters
            </button>
          </div>
        </Card>}

      <Card data-id="element-6576">
        <Table columns={columns} data={filteredLogs} keyField="id" actions={log => <div className="flex justify-end" data-id="element-6578">
              <button onClick={() => handleViewDetails(log)} className="p-1 rounded-full text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30" title="View Details" data-id="element-6579">
                <EyeIcon className="h-5 w-5" data-id="element-6580" />
              </button>
            </div>} data-id="element-6577" />
      </Card>

      {/* Detail Modal */}
      <Modal isOpen={showDetailModal} onClose={() => setShowDetailModal(false)} title="Audit Log Details" size="lg" data-id="element-6581">
        {selectedLog && <div className="space-y-4" data-id="element-6582">
            <div className="flex items-center space-x-3" data-id="element-6583">
              {selectedLog.status === 'success' ? <CheckCircleIcon className="h-6 w-6 text-green-500" data-id="element-6584" /> : <XCircleIcon className="h-6 w-6 text-red-500" data-id="element-6585" />}
              <div data-id="element-6586">
                <h3 className="text-lg font-medium" data-id="element-6587">{selectedLog.action}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-6588">
                  {formatDateTime(selectedLog.timestamp)}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-6589">
              <div data-id="element-6590">
                <h4 className="font-medium mb-2" data-id="element-6591">User Information</h4>
                <div className="space-y-2 text-sm" data-id="element-6592">
                  <p data-id="element-6593">
                    <span className="font-medium" data-id="element-6594">Name:</span>{' '}
                    {selectedLog.userName}
                  </p>
                  <p data-id="element-6595">
                    <span className="font-medium" data-id="element-6596">ID:</span>{' '}
                    {selectedLog.userId}
                  </p>
                  <p data-id="element-6597">
                    <span className="font-medium" data-id="element-6598">Role:</span>{' '}
                    {selectedLog.role}
                  </p>
                  <p data-id="element-6599">
                    <span className="font-medium" data-id="element-6600">IP Address:</span>{' '}
                    {selectedLog.ipAddress}
                  </p>
                </div>
              </div>
              <div data-id="element-6601">
                <h4 className="font-medium mb-2" data-id="element-6602">Action Information</h4>
                <div className="space-y-2 text-sm" data-id="element-6603">
                  <p data-id="element-6604">
                    <span className="font-medium" data-id="element-6605">Module:</span>{' '}
                    {selectedLog.module}
                  </p>
                  <p data-id="element-6606">
                    <span className="font-medium" data-id="element-6607">Object ID:</span>{' '}
                    {selectedLog.objectId}
                  </p>
                  <p data-id="element-6608">
                    <span className="font-medium" data-id="element-6609">Status:</span>{' '}
                    {selectedLog.status}
                  </p>
                  <p data-id="element-6610">
                    <span className="font-medium" data-id="element-6611">Remarks:</span>{' '}
                    {selectedLog.remarks}
                  </p>
                </div>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-6612">
              <h4 className="font-medium mb-2" data-id="element-6613">Details</h4>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md overflow-x-auto" data-id="element-6614">
                <pre className="text-xs text-gray-800 dark:text-gray-200" data-id="element-6615">
                  {JSON.stringify(selectedLog.details, null, 2)}
                </pre>
              </div>
            </div>
          </div>}
      </Modal>
    </div>;
};
export default AuditLogs;